import React, { Fragment, useState, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import Box from '../../elements/Box';
import Text from '../../elements/Text';
import Button from '../../elements/Button';
import Tooltip from './atoms/LocationTooltip';
import ErrorBoundary from '../../utils/ErrorBoundary';
import { isSSR } from '@front10/utils';

function MapRC(props) {
  const {
    accessToken,
    points,
    enableRoute,
    lineStyle,
    leafletProps,
    urlLayerTemplate,
    heightContainer,
    widthContainer,
    disclaimer,
    zoom,
    onClickMarker,
    onClickZoom,
    onCloseTooltip,
    useErrorBoundary,
    errorFallback,
    showOverlay,
    overlayLabelKey,
    overlayProps,
    overlayLabelProps,
    popupClasses,
    tooltipStyles,
    isMobile,
    closeButtonStyles,
    closeIconStyles,
    showClosePopupButton,
    onStationClick
  } = props;

  const { translate, direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const [overlay, setOverlay] = useState(showOverlay && isMobile);
  const [interactive, setInteractive] = useState(false);
  let pointsRoute = [];

  function getCoordinatesAverage() {
    let long = 0;
    let lat = 0;
    points.forEach((point) => {
      long += parseFloat(point.longitude, 10);
      lat += parseFloat(point.latitude, 10);
    });
    return [lat / points.length, long / points.length];
  }

  function getDefaultViewport() {
    return {
      center: getCoordinatesAverage(),
      zoom
    };
  }

  function getMapPoints() {
    return points.map((point) => [point.latitude, point.longitude]);
  }

  function getRoutesMarkers() {
    const point = [];
    if (points.length > 0) {
      point.push(points[0]);
      point.push(points[points.length - 1]);
    }
    return point;
  }

  function getMarker() {
    if (!isSSR()) {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const Leaflet = require('leaflet');
      const { iconUrl, iconSize, iconAnchor } = props;
      /* eslint new-cap: ["error", { "newIsCap": false }] */
      return new Leaflet.icon({
        iconUrl,
        iconSize,
        iconAnchor
      });
    }
    return null;
  }

  function handleZoomOnClick(oldZoom, newZoom) {
    onClickZoom(oldZoom, newZoom);
  }

  const [keepPopupOpen, setKeepPopupOpen] = useState(false);
  function renderMarker(Marker, Popup, point) {
    return (
      <Marker
        key={`${point.latitude}_${point.longitude}`}
        position={[point.latitude, point.longitude]}
        onClick={(e) => {
          onClickMarker(point.extraInfo);
          setKeepPopupOpen(true);
        }}
        icon={getMarker()}
        onmouseover={(e) => {
          if (!isMobile) {
            e.target.openPopup();
          }
        }}
        onmouseout={(e) => {
          if (!keepPopupOpen) {
            e.target.closePopup();
          }
        }}
      >
        <Popup
          className={popupClasses}
          closeButton={showClosePopupButton}
          onClose={() => {
            onCloseTooltip(point);
            setKeepPopupOpen(false);
          }}
          ref={handlePopupRef}
        >
          <Tooltip
            {...tooltipStyles}
            data={point.info}
            station={point.extraInfo}
            onStationClick={onStationClick}
          />
        </Popup>
      </Marker>
    );
  }

  const mapRef = useRef(null);
  useEffect(() => {
    const markers = document.querySelectorAll('.leaflet-marker-icon');
    if (markers && markers.length !== 0) {
      for (let i = 0; i < markers.length; i++) {
        markers[i].addEventListener('click', (e) => {
          handlePopupRef.current.leafletElement.options.leaflet.map.openPopup();
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current]);

  const handlePopupRef = React.useRef(null);
  const zoomValue = useRef(null);

  if (!isSSR()) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { Map, TileLayer, Marker, Popup, Polyline } = require('react-leaflet');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { latLngBounds } = require('leaflet');
    const mapPoints = getMapPoints();
    let mapProperties = {};
    if (points.length > 1) {
      mapProperties.bounds = latLngBounds(mapPoints);
    } else {
      mapProperties = getDefaultViewport();
    }

    if (mapProperties.bounds) {
      const p = mapProperties.bounds;
      const se = p.getSouthEast();
      const nw = p.getNorthWest();
      if (Math.abs(se.lat - nw.lat) < 0.00001 && Math.abs(se.lng - nw.lng) < 0.00001) {
        mapProperties = getDefaultViewport();
      }
    }
    mapProperties = { ...mapProperties, ...leafletProps };
    const Boundary = useErrorBoundary ? ErrorBoundary : Fragment;

    if (enableRoute) {
      pointsRoute = getRoutesMarkers();
    }
    return (
      <Boundary fallback={errorFallback}>
        <Box position="relative" className={` ${className} ${MapRC.componentName}`} style={style}>
          {showOverlay && overlay && (
            <Box
              className="animation-fade"
              onMouseLeave={() => !isMobile && setOverlay(false)}
              onClick={() => {
                setInteractive(true);
                setOverlay(false);
              }}
              {...overlayProps}
            >
              <Text {...overlayLabelProps}>
                {translate(overlayLabelKey, 'Click to activate interactivity on the map')}
              </Text>
            </Box>
          )}
          {showOverlay && !overlay && isMobile && (
            <Button
              onClick={() => {
                setInteractive(false);
                setOverlay(true);
              }}
              {...closeButtonStyles}
            >
              <Text {...closeIconStyles}>{translate('cmp-map-close-text', '+')}</Text>
            </Button>
          )}
          {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
          <Map
            onViewportChange={(e) => {
              if (zoomValue.current === null) {
                zoomValue.current = e.zoom;
              }
            }}
            ref={mapRef}
            onZoomEnd={() => {
              handleZoomOnClick(zoomValue.current, mapRef.current.leafletElement.getZoom());
              zoomValue.current = mapRef.current.leafletElement.getZoom();
            }}
            onMouseOver={() => !isMobile && !overlay && !interactive && setOverlay(true)}
            onMouseOut={() => !isMobile && setInteractive(false)}
            onContextMenu={() => {
              if (isMobile) {
                setInteractive(false);
                setOverlay(true);
              }
            }}
            style={{ height: heightContainer, width: widthContainer }}
            {...mapProperties}
          >
            {enableRoute && (
              <>
                {pointsRoute.map((pointRoute) => renderMarker(Marker, Popup, pointRoute))}
                <Polyline {...lineStyle} positions={mapPoints} />
              </>
            )}
            {!enableRoute && points && points.map((point) => renderMarker(Marker, Popup, point))}
            <TileLayer
              attribution={disclaimer}
              url={`${urlLayerTemplate}${accessToken ? `?access_token=${accessToken}` : ``}`}
            />
          </Map>
        </Box>
      </Boundary>
    );
  }
  return <Box>SSR is not supported</Box>;
}

MapRC.propTypes = {
  /**
   * token to get access to the MapBox API
   */
  accessToken: PropTypes.string,
  /**
   * Disclaimer text
   */
  disclaimer: PropTypes.string,
  /**
   * zoom by default in the map
   */
  zoom: PropTypes.number,
  /**
   * Determine if show close popup button
   */
  showClosePopupButton: PropTypes.bool,
  /**
   * Used to draw a line between all points.
   */
  enableRoute: PropTypes.bool,
  /**
   * array of points to mark on the map
   */
  points: PropTypes.arrayOf(PropTypes.any),
  /**
   * height of the map
   */
  heightContainer: PropTypes.string,
  /**
   * width of the map
   */
  widthContainer: PropTypes.string,
  /**
   * popup styles
   */
  popupClasses: PropTypes.objectOf(PropTypes.any),
  /**
   * tooltip styles
   */
  tooltipStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Marker url
   */
  iconUrl: PropTypes.string,
  /**
   * size of the marker
   */
  iconSize: PropTypes.arrayOf(PropTypes.number),
  /**
   * marker size
   */
  iconAnchor: PropTypes.arrayOf(PropTypes.number),
  /**
   * Styles applied to the line
   * https://leafletjs.com/reference-1.5.0.html#path
   */
  lineStyle: PropTypes.objectOf(PropTypes.any),
  /**
   * Leaflet props
   */
  leafletProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Url path of the layers engine.
   */
  urlLayerTemplate: PropTypes.string,
  /**
   * fired when click on the marker
   */
  onClickMarker: PropTypes.func,
  /**
   * fired when click on the zoom
   */
  onClickZoom: PropTypes.func,
  /**
   * firef when click on close of the marker
   */
  onCloseTooltip: PropTypes.func,
  /**
   * Wether or not to use an error boundary
   */
  useErrorBoundary: PropTypes.bool,
  /**
   * View to use as a fallback in error state
   */
  errorFallback: PropTypes.elementType,
  /**
   * used to change text label in overlay
   */
  overlayLabelKey: PropTypes.string,
  /**
   * overlay Props
   */
  overlayProps: PropTypes.objectOf(PropTypes.any),
  /**
   * overlay labels props
   */
  overlayLabelProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Used to show or not overlay layer
   */
  showOverlay: PropTypes.bool,
  /**
   * Use this props to mobile behavior
   */
  isMobile: PropTypes.bool,
  closeButtonStyles: PropTypes.objectOf(PropTypes.any),
  closeIconStyles: PropTypes.objectOf(PropTypes.any),
  onStationClick: PropTypes.func
};
MapRC.defaultProps = {
  accessToken: null,
  showClosePopupButton: true,
  zoom: 5,
  disclaimer: '',
  enableRoute: false,
  points: [],
  popupClasses: {},
  tooltipStyles: {},
  iconUrl:
    'https://res.cloudinary.com/dakp804eh/image/upload/v1562594918/airmodules/Utils/maps-24x24.png',
  iconSize: [24, 24],
  iconAnchor: [13, 0],
  heightContainer: '500px',
  widthContainer: '100%',
  useErrorBoundary: true,
  errorFallback: undefined,
  lineStyle: {
    color: '#034166',
    stroke: true,
    weight: 6,
    opacity: 1,
    lineCap: 'round',
    lineJoin: 'round',
    dashArray: null,
    dashOffset: null,
    fill: false,
    fillColor: 'red',
    fillOpacity: 1,
    fillRule: 'evenodd',
    className: 'line-style-map-component'
  },
  leafletProps: {},
  onCloseTooltip: () => {},
  onClickMarker: () => {},
  onClickZoom: () => {},
  urlLayerTemplate: 'https://api.mapbox.com/v4/mapbox.streets/{z}/{x}/{y}.png',
  overlayProps: {
    position: 'absolute',
    width: '100',
    height: '100',
    display: 'flex',
    style: {
      zIndex: '3',
      cursor: 'pointer',
      backgroundColor: 'rgba(0,0,0,.5)'
    }
  },
  overlayLabelKey: 'cmp-map-desktop-activate-interactive-message',
  overlayLabelProps: {
    marginWidth: 'auto',
    marginHeight: 'auto',
    fontSize: '30px',
    textColor: 'white',
    textAlign: 'center',
    style: {
      textShadow: '2px 2px 2px rgba(0,0,0,.5)'
    }
  },
  rounded: '@theme:card.rounded',
  showOverlay: true,
  isMobile: false,
  closeButtonStyles: {
    position: 'absolute',
    marginTop: '10px',
    marginRight: '10px',
    right: '0',
    color: 'transparent',
    style: {
      boxShadow: '0 0 0 2px rgba(0,0,0,0.2)',
      backgroundColor: 'white',
      borderRadius: '2px',
      zIndex: 3,
      width: '30px',
      height: '30px'
    }
  },
  closeIconStyles: {
    justifyContent: 'center',
    marginTop: '-4px',
    display: 'flex',
    fontSize: '22px',
    style: {
      font: "bold 22px 'Lucida Console', Monaco, monospace",
      transform: 'rotate(45deg)'
    }
  }
};
MapRC.componentName = 'MapRC';

export default MapRC;
